import React, { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  Star,
  Briefcase,
  FileText,
  Gauge,
  Target,
  Wrench,
  Film,
  PenLine,
  Users,
  ArrowRight,
  Lock,
  Check,
  X,
  ExternalLink,
} from "lucide-react";

const ICONS = [Briefcase, FileText, Gauge, Target, Wrench, Film, PenLine, Users];
import { getMembers } from "@/lib/api";
import { usePrice } from "@/lib/price";
const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/* ───────── Falling icons (same as Hero) ───────── */
function FallingIcons() {
  const items = Array.from({ length: 12 }, (_, i) => ({
    Icon: ICONS[i % ICONS.length],
    left: (i * 67) % 100,
    delay: (i * 0.9) % 10,
    duration: 16 + ((i * 3) % 16),
    size: 22 + ((i * 7) % 22),
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {items.map((it, i) => {
        const Icon = it.Icon;
        return (
          <Icon
            key={i}
            size={it.size}
            strokeWidth={2.5}
            className="absolute text-[#0a0a0a]/[0.07]"
            style={{
              left: `${it.left}%`,
              top: "-40px",
              animation: `drift ${it.duration}s linear ${it.delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

/* ───────── Hero ───────── */
export function ToolHero({ kicker, title, sub, image, primary, secondary, statNum, statLabel }) {
  const { price } = usePrice();
  const [members, setMembers] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await getMembers();
        const c = j?.count ?? j?.members;
        if (alive && c != null) setMembers(Number(c).toLocaleString());
      } catch {
        /* silent */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="top"
      data-testid="tool-hero"
      className="relative overflow-hidden border-b-4 border-[#0a0a0a] bg-[#FDFBF7] bg-grid"
    >
      <FallingIcons />
      <div className="pointer-events-none absolute -top-20 -right-24 w-[340px] h-[340px] rounded-full bg-[#FFE600] border-4 border-[#0a0a0a] hidden md:block" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-[240px] h-[240px] bg-[#0055FF] border-4 border-[#0a0a0a] rotate-12 hidden md:block" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm">
              <span className="live-dot" /> {kicker}
            </div>
            <h1
              data-testid="tool-hero-title"
              className="font-display mt-6 text-[40px] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.92] md:leading-[0.9]"
            >
              {title}
            </h1>
            <p className="mt-6 max-w-xl font-mono-body text-base md:text-lg text-[#0a0a0a]/80 leading-relaxed">
              {sub}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
              <a
                href={primary.href}
                data-testid="tool-hero-cta-primary"
                {...(primary.href.startsWith("http") && {
                  target: "_blank",
                  rel: "noopener noreferrer",
                })}
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
              >
                {primary.icon === "lock" && <Lock size={18} strokeWidth={3} />}
                {primary.label}
                {primary.href.startsWith("http") ? (
                  <ExternalLink size={16} strokeWidth={3} />
                ) : (
                  <ArrowUpRight size={18} strokeWidth={3} />
                )}
              </a>
              {secondary && (
                <a
                  href={secondary.href}
                  data-testid="tool-hero-cta-secondary"
                  {...(secondary.href.startsWith("http") && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                  className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
                >
                  {secondary.label}
                </a>
              )}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono-body text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[#0055FF]" />
                One-time {price} · lifetime
              </div>
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#FF3B30]" />7-day Whop refund
              </div>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={12} fill="#FFE600" strokeWidth={1.5} className="text-[#0a0a0a]" />
                ))}
                <span className="font-bold">{members || "1,762"} hackers</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="border-4 border-[#0a0a0a] shadow-brutal-lg overflow-hidden bg-white">
                <img
                  data-testid="tool-hero-image"
                  src={image}
                  alt={title}
                  className="w-full h-auto object-cover"
                />
              </div>
              {statNum && (
                <div className="absolute -top-4 -left-4 md:-top-5 md:-left-8 bg-[#00FF66] border-4 border-[#0a0a0a] px-3 py-2 md:px-4 md:py-3 shadow-brutal -rotate-[8deg] wiggle">
                  <div className="font-display text-xl md:text-3xl leading-none">
                    {statNum}
                  </div>
                  <div className="font-accent text-[10px] md:text-xs">{statLabel}</div>
                </div>
              )}
              <div className="absolute -bottom-4 -right-2 md:-bottom-6 md:-right-6 bg-[#FF3B30] text-white border-4 border-[#0a0a0a] px-3 py-2 md:px-4 md:py-3 shadow-brutal rotate-[6deg]">
                <div className="font-display text-lg md:text-2xl leading-none">{price} ONCE</div>
                <div className="font-accent text-sm md:text-base">LIFETIME</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── 3 brutalist cards ───────── */
const CARD_PALETTE = [
  { bg: "bg-[#FFE600]", fg: "text-[#0a0a0a]" },
  { bg: "bg-[#0055FF]", fg: "text-white" },
  { bg: "bg-[#00FF66]", fg: "text-[#0a0a0a]" },
];

export function ThreeCards({ kicker, title, cards }) {
  return (
    <section
      data-testid="three-cards"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 md:mb-14 reveal">
          {kicker && (
            <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
              {kicker}
            </div>
          )}
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            {title}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cards.map((c, i) => {
            const p = CARD_PALETTE[i % CARD_PALETTE.length];
            return (
              <div
                key={c.title}
                data-testid={`three-card-${i}`}
                className={`${p.bg} ${p.fg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-7 reveal`}
              >
                <div className="font-display text-7xl leading-none">0{i + 1}</div>
                <div className="font-display text-2xl md:text-3xl mt-6 leading-tight">
                  {c.title}
                </div>
                <p className="font-mono-body text-sm md:text-base mt-3 leading-relaxed opacity-90">
                  {c.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────── Two-option split (Resume: keep format vs take ours) ───────── */
export function SplitTwo({ kicker, title, left, right }) {
  return (
    <section
      data-testid="split-two"
      className="border-b-4 border-[#0a0a0a] bg-[#FFE600]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 reveal">
          {kicker && (
            <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
              {kicker}
            </div>
          )}
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            {title}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {[left, right].map((opt, i) => (
            <div
              key={opt.title}
              className={`${i === 0 ? "bg-white" : "bg-[#0a0a0a] text-[#FFE600]"} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-7 md:p-8 reveal`}
            >
              <div className="font-accent text-xs inline-block px-2.5 py-1 mb-4 border-2 border-[#0a0a0a] bg-[#FFE600] text-[#0a0a0a]">
                OPTION {String.fromCharCode(65 + i)}
              </div>
              <div className="font-display text-2xl md:text-3xl mb-3">
                {opt.title}
              </div>
              <p className="font-mono-body text-sm md:text-base leading-relaxed opacity-90">
                {opt.body}
              </p>
              <ul className="space-y-2 mt-5">
                {opt.bullets?.map((b) => (
                  <li key={b} className="flex items-start gap-2 font-mono-body text-sm">
                    <Check size={14} strokeWidth={3} className="mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Before / after (Resume) ───────── */
export function BeforeAfter() {
  return (
    <section
      data-testid="before-after"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 reveal">
          <div className="font-accent text-sm bg-[#0055FF] text-white inline-block px-3 py-1 mb-4">
            BEFORE / AFTER
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            Other tools <span className="bg-[#FFE600] px-2">talk</span> about
            <br />
            your resume.{" "}
            <span className="bg-[#0055FF] text-white px-2">We rebuild it.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-7 reveal">
            <div className="font-accent text-xs bg-[#FF3B30] text-white inline-block px-2.5 py-1 mb-4">
              BEFORE — OTHER TOOLS
            </div>
            <div className="font-mono-body text-[13px] text-[#0a0a0a]/70 space-y-2 leading-relaxed">
              <p>
                <span className="text-[#FF3B30] font-bold">✗</span> &ldquo;Your resume could
                use stronger action verbs in section 2.&rdquo;
              </p>
              <p>
                <span className="text-[#FF3B30] font-bold">✗</span> &ldquo;Try adding
                quantifiable achievements.&rdquo;
              </p>
              <p>
                <span className="text-[#FF3B30] font-bold">✗</span> &ldquo;Consider tailoring
                your summary for this role.&rdquo;
              </p>
              <p className="pt-2 italic">
                (You still have to write it yourself.)
              </p>
            </div>
          </div>
          <div className="bg-[#00FF66] border-4 border-[#0a0a0a] shadow-brutal p-7 reveal">
            <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFE600] inline-block px-2.5 py-1 mb-4">
              AFTER — JOBHACKI
            </div>
            <div className="font-mono-body text-[13px] space-y-2 leading-relaxed">
              <p>
                <span className="text-[#0055FF] font-bold">✓</span> Paste the job posting.
              </p>
              <p>
                <span className="text-[#0055FF] font-bold">✓</span> Drop in your old resume
                or sparse details.
              </p>
              <p>
                <span className="text-[#0055FF] font-bold">✓</span> Get back a finished,
                ATS-tuned resume — keywords, structure, hyperlink certifications.
              </p>
              <p>
                <span className="text-[#0055FF] font-bold">✓</span> Export to clean Word/PDF.
              </p>
              <p className="pt-2 font-bold">
                You don&apos;t prompt — you ship.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── ATS source logos (Jobs) ───────── */
const ATS_SOURCES = [
  { name: "Greenhouse", domain: "greenhouse.io" },
  { name: "Lever", domain: "lever.co" },
  { name: "Ashby", domain: "ashbyhq.com" },
  { name: "Workday", domain: "workday.com" },
  { name: "SmartRecruiters", domain: "smartrecruiters.com" },
  { name: "Workable", domain: "workable.com" },
];

export function SourceLogos() {
  return (
    <section
      data-testid="source-logos"
      className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
        <div className="text-center mb-10 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            SOURCES
          </div>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95]">
            We watch the boards
            <br />
            <span className="text-[#FFE600]">companies actually hire on.</span>
          </h2>
          <p className="font-mono-body text-sm md:text-base mt-4 text-[#FDFBF7]/70">
            First-party ATS sources. No scrapers. No reposts.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 reveal">
          {ATS_SOURCES.map((s) => (
            <div
              key={s.name}
              data-testid={`source-${s.name.toLowerCase()}`}
              className="bg-white text-[#0a0a0a] border-4 border-[#FDFBF7] shadow-brutal-sm p-4 flex flex-col items-center justify-center gap-2 aspect-[3/2]"
            >
              <img
                src={`https://icon.horse/icon/${s.domain}`}
                alt={s.name}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
              <span className="font-display text-sm md:text-base">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Card grid (Playbooks featured) ───────── */
export function CardGrid({ kicker, title, items }) {
  return (
    <section
      data-testid="card-grid"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="mb-10 md:mb-14 reveal">
          {kicker && (
            <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
              {kicker}
            </div>
          )}
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[0.95]">
            {title}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => {
            const p = [
              { bg: "bg-[#FFE600]", fg: "text-[#0a0a0a]" },
              { bg: "bg-[#0055FF]", fg: "text-white" },
              { bg: "bg-[#FF3B30]", fg: "text-white" },
              { bg: "bg-[#00FF66]", fg: "text-[#0a0a0a]" },
              { bg: "bg-[#FF00FF]", fg: "text-white" },
              { bg: "bg-white", fg: "text-[#0a0a0a]" },
            ][i % 6];
            return (
              <article
                key={it.title}
                data-testid={`grid-item-${i}`}
                className={`${p.bg} ${p.fg} border-4 border-[#0a0a0a] shadow-brutal hover-pop p-6 flex flex-col reveal`}
              >
                <span className="font-accent text-[10px] bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-2 py-0.5 self-start mb-3">
                  {it.tag}
                </span>
                <h3 className="font-display text-xl md:text-2xl leading-tight mb-2">
                  {it.title}
                </h3>
                <p className="font-mono-body text-[13px] opacity-85 flex-1">
                  {it.body}
                </p>
                <div className="mt-5 flex items-center justify-between font-accent text-xs border-t-2 border-dashed border-[#0a0a0a]/30 pt-3">
                  <span>{it.meta || "GROUNDED"}</span>
                  <ArrowUpRight size={16} strokeWidth={3} />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────── Access band (free w/ account) ───────── */
export function AccessBand({ title, body, ctaFree = "/login" }) {
  return (
    <section data-testid="access-band" className="border-b-4 border-[#0a0a0a] bg-[#00FF66]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-3">
            FREE WITH AN ACCOUNT
          </div>
          <h3 className="font-display text-3xl md:text-5xl leading-[0.95]">
            {title}
          </h3>
          <p className="font-mono-body mt-3 text-[#0a0a0a]/85 text-base">{body}</p>
        </div>
        <div className="md:col-span-4 flex md:justify-end reveal">
          <a
            href={ctaFree}
            data-testid="access-band-cta"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base shadow-brutal"
          >
            Start free <ArrowRight size={16} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ───────── Upsell band (Toolbox) ───────── */
export function UpsellBand({ title, body }) {
  const { price } = usePrice();
  return (
    <section data-testid="upsell-band" className="border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-3">
            ALL-ACCESS UPSELL
          </div>
          <h3 className="font-display text-3xl md:text-5xl leading-[0.95]">{title}</h3>
          <p className="font-mono-body mt-3 text-[#FDFBF7]/75 text-base">{body}</p>
        </div>
        <div className="md:col-span-4 flex md:justify-end reveal">
          <a
            href={WHOP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="upsell-cta"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#FDFBF7] px-6 py-4 font-accent text-base shadow-[6px_6px_0_0_#FDFBF7]"
          >
            <Lock size={16} strokeWidth={3} /> Unlock for {price}
            <ExternalLink size={14} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ───────── Waitlist (OmniScript) ───────── */
export function WaitlistBand({ title }) {
  const { price } = usePrice();
  return (
    <section data-testid="waitlist-band" className="border-b-4 border-[#0a0a0a] bg-[#FF00FF] text-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-3">
            SOON · WAITLIST
          </div>
          <h3 className="font-display text-3xl md:text-5xl leading-[0.95]">{title}</h3>
          <p className="font-mono-body mt-3 text-white/85 text-base">
            All-Access ({price} one-time) puts you first in line the moment OmniScript ships.
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end flex-col gap-3 reveal">
          <a
            href={WHOP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="waitlist-cta-lock"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base shadow-brutal"
          >
            <Lock size={16} strokeWidth={3} /> Lock {price} + notify
          </a>
          <a
            href="/login"
            data-testid="waitlist-cta-free"
            className="hover-pop inline-flex items-center justify-center gap-2 bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-3.5 font-accent text-sm shadow-brutal-sm"
          >
            Notify me (free)
          </a>
        </div>
      </div>
    </section>
  );
}

/* ───────── Pricing band ───────── */
export function PricingBand({ title, body }) {
  const { price, remaining, cap } = usePrice();
  return (
    <section
      id="pricing"
      data-testid="pricing-band"
      className="relative border-b-4 border-[#0a0a0a] bg-[#FDFBF7] overflow-hidden"
    >
      <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-[#FFE600] border-4 border-[#0a0a0a] hidden md:block" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          <div className="lg:col-span-5 reveal flex flex-col gap-3 justify-center">
            <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 self-start">
              ONE PAYMENT · NOT A SUBSCRIPTION
            </div>
            <h3 className="font-display text-[36px] sm:text-5xl md:text-6xl leading-[0.95]">
              {title || (
                <>
                  Start <span className="bg-[#FFE600] px-2">free.</span>
                  <br />
                  Unlock everything for{" "}
                  <span className="bg-[#FF3B30] text-white px-2">{price}</span>.
                </>
              )}
            </h3>
            {body && (
              <p className="font-mono-body text-[#0a0a0a]/80 mt-2 max-w-md">{body}</p>
            )}
          </div>
          <div
            data-testid="pricing-card"
            className="lg:col-span-7 relative bg-[#FF3B30] text-white border-4 border-[#0a0a0a] shadow-brutal-lg p-7 md:p-9 reveal"
          >
            <span className="absolute -top-5 -left-3 bg-[#00FF66] text-[#0a0a0a] border-4 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs shadow-brutal rotate-[-6deg]">
              {remaining && cap ? (
                <>
                  {remaining.toLocaleString()} OF {cap.toLocaleString()} LAUNCH SPOTS LEFT
                </>
              ) : (
                <>FOUNDING PRICE · FIRST 2,500</>
              )}
            </span>
            <div className="flex items-end gap-3 flex-wrap mt-2">
              <div className="font-display text-[96px] sm:text-[120px] md:text-[160px] leading-[0.82]">
                {price}
              </div>
              <div className="font-mono-body text-sm md:text-base pb-5">
                <span className="line-through opacity-60">$49</span>
                <br />
                one-time · lifetime
              </div>
            </div>
            <p className="font-mono-body text-[14px] mt-3">
              All 9 tools. 7-day Whop refund. No recurring charge.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3.5">
              <a
                href={WHOP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-band-lock"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
              >
                <Lock size={18} strokeWidth={3} /> LOCK {price}
                <ExternalLink size={14} strokeWidth={3} />
              </a>
              <a
                href="/login"
                data-testid="pricing-band-free"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base md:text-lg shadow-brutal"
              >
                Start free
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Final CTA band ───────── */
export function FinalCTABand({ kicker, title, ctaPrimary, ctaSecondary, bg = "bg-[#FFE600]" }) {
  return (
    <section
      data-testid="final-cta-band"
      className={`relative border-b-4 border-[#0a0a0a] ${bg} overflow-hidden`}
    >
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <div className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-3 py-1.5 font-accent text-xs md:text-sm shadow-brutal-sm mb-7">
          {kicker}
        </div>
        <h2 className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.92]">
          {title}
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={ctaPrimary.href}
            data-testid="finalband-cta-primary"
            {...(ctaPrimary.href.startsWith("http") && {
              target: "_blank",
              rel: "noopener noreferrer",
            })}
            className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
          >
            {ctaPrimary.label}{" "}
            {ctaPrimary.href.startsWith("http") ? (
              <ExternalLink size={18} strokeWidth={3} />
            ) : (
              <ArrowUpRight size={20} strokeWidth={3} />
            )}
          </a>
          {ctaSecondary && (
            <a
              href={ctaSecondary.href}
              data-testid="finalband-cta-secondary"
              {...(ctaSecondary.href.startsWith("http") && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-7 py-5 font-accent text-lg md:text-xl shadow-brutal"
            >
              {ctaSecondary.label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/* eslint-disable-next-line no-unused-vars */
const _UNUSED = { Check, X };
